import { useSearchParams } from "@solidjs/router";
import { createMemo, createSignal, Show } from "solid-js";
import { SCAN_SECTION_KEY } from "../../enums/scan";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { LifecycleBadge, SeverityBadge, StatusBadge } from "../../ui/Badge";
import { singleParam } from "../../ui/searchParam";
import { IssueEditor } from "../admin/IssueEditor";
import { issueBriefEligibility } from "../brief/briefIssueAdapter";
import { hasSessionIssueBrief } from "../brief/briefSession";
import { createWebsiteStore } from "../website/store";
import { AffectedPagesTable } from "./AffectedPagesTable";
import { IssueDetailDisclosures } from "./IssueDetailDisclosures";
import { IssueDetailPanels } from "./IssueDetailPanels";
import { requireData } from "./issueDetailParts";
import {
  currentIssuePageGroups,
  usesDemoPageFallback,
} from "./issuePageResolution";
import { clientIssueTitle } from "./issueText";
import { issueDetailReturnContext } from "./pageDetailNavigation";
import { ScanBreadcrumbs, ScanRouteSurface } from "./ScanRouteChrome";
import { ScanShell } from "./ScanShell";

export function ScanIssueDetail(props: { websiteId: string; issueId: string }) {
  setScope(KEYBOARD_SCOPE.LIST);
  const context = createWebsiteStore(props.websiteId);
  const [editing, setEditing] = createSignal(false);
  const data = () => context.data();
  const issue = createMemo(
    () => data()?.issues.find((row) => row.id === props.issueId) ?? null,
  );
  const pageGroups = createMemo(() => {
    const current = data();
    const currentIssue = issue();
    return current && currentIssue
      ? currentIssuePageGroups(current, currentIssue)
      : [];
  });
  const [search] = useSearchParams();
  const returnContext = createMemo(() =>
    issueDetailReturnContext(props.websiteId, props.issueId, {
      source: singleParam(search.from) ?? null,
      returnQuery: singleParam(search.return) ?? null,
      dimensionKey: singleParam(search.dimension) ?? null,
      dimensionOrigin: singleParam(search.dimensionFrom) ?? null,
      pageId: singleParam(search.page) ?? null,
    }),
  );

  return (
    <ScanShell context={context} active={SCAN_SECTION_KEY.ISSUES}>
      <Show
        when={data()}
        fallback={<div class="loading">Loading issue...</div>}
      >
        <Show
          when={issue()}
          keyed
          fallback={
            <ScanRouteSurface class="signal-issue-detail-route">
              <ScanBreadcrumbs
                items={[
                  { label: "Signal Scan", href: `/w/${props.websiteId}/scan` },
                  {
                    label: "Issues",
                    href: `/w/${props.websiteId}/scan/issues`,
                  },
                  { label: "Issue not found" },
                ]}
              />
              <section class="scan-panel">
                <h1 class="scan-title">Issue not found</h1>
                <p>
                  The selected issue is not present in the current scan data.
                </p>
              </section>
            </ScanRouteSurface>
          }
        >
          {(currentIssue) => (
            <ScanRouteSurface class="signal-issue-detail-route">
              <ScanBreadcrumbs
                items={[
                  {
                    label: "Signal Scan",
                    href: `/w/${props.websiteId}/scan`,
                  },
                  {
                    label: returnContext().parentLabel,
                    href: returnContext().href,
                  },
                  { label: clientIssueTitle(currentIssue) },
                ]}
              />
              <div class="scan-detail-head">
                <div class="scan-detail-title-row">
                  <div>
                    <span class="scan-kicker">Issue details</span>
                    <h1 class="scan-title">{clientIssueTitle(currentIssue)}</h1>
                  </div>
                  <div class="scan-drawer-badges">
                    <SeverityBadge severity={currentIssue.severity} />
                    <StatusBadge status={currentIssue.status} />
                    <LifecycleBadge state={currentIssue.lifecycleState} />
                  </div>
                </div>
              </div>

              <Show
                when={usesDemoPageFallback(
                  pageGroups().flatMap((group) => group.resolutions),
                )}
              >
                <div class="scan-note">
                  Page matching note: some affected pages were matched by URL
                  path because their stored IDs differ from this scan.
                </div>
              </Show>

              <IssueDetailPanels
                data={requireData(data())}
                issue={currentIssue}
                context={context}
                briefEligibility={issueBriefEligibility(
                  requireData(data()),
                  currentIssue,
                )}
                briefCreated={hasSessionIssueBrief(
                  props.websiteId,
                  currentIssue.id,
                )}
                briefHref={`/w/${props.websiteId}/brief?source=issue&issueId=${encodeURIComponent(currentIssue.id)}`}
                onEdit={() => setEditing(true)}
              />

              <AffectedPagesTable
                data={requireData(data())}
                websiteId={props.websiteId}
                issueId={currentIssue.id}
                pageGroups={pageGroups()}
              />

              <IssueDetailDisclosures
                data={requireData(data())}
                issue={currentIssue}
              />
              <Show when={editing()}>
                <IssueEditor
                  context={context}
                  issue={currentIssue}
                  onClose={() => setEditing(false)}
                />
              </Show>
            </ScanRouteSurface>
          )}
        </Show>
      </Show>
    </ScanShell>
  );
}
